<script setup lang="ts">
import { Mask } from 'maska';
import type Address from './Address.vue';
import type AntiRobot from '@/components/base/form/AntiRobot.vue';
import type StrengthBar from '@/components/auth/StrengthBar.vue';
import type { UserDocument } from '@/types/UserData';
import { useUserJourney } from '~/composables/apostatudo/use-user-journey';
import IconWrapper from '@/components/base/IconWrapper.vue';

const { $metrics } = useNuxtApp();
const { t } = useTexts();
const { openToaster } = useLayoutStore();
const { referenceCode } = useParameters();
const { ApiService } = useApiService();
const { getFormattedBrazilianDate } = useDateTimeHelper();
const { isSocialAuthEnabled, isLudopathyValidationEnabled } =
  useFeatureFlagsNew();

const { registerUserEvent } = useUserJourney();

const {
  isCaptchaRegisterEnabled,
  isCaptchaValidateCpfEnabled,
  isInvisibleCaptcha,
} = useCaptcha();

const {
  isRegisterRequestDocument,
  isRequestAddressOnRegister,
  isRegisterRequestBirthDate,
  isRegisterRequestPhone,
  isSingleStepirstOnlyCpfEnabled,
  isAuthV2FlowEnabled,
} = useAuth();

const { isAvailableEnvironment: isNativeAppEnv } = useBiometricAuth();
const { rememberLastCredentials } = useBiometricEnrollment();

const {
  isSocialAuthFlow,
  socialEmail,
  socialToken,
  socialService,
  clearSocialData,
} = useAuthSocial();

const registerData = ref<any>({
  cpf: '',
  birth_date: '',
  name: '',
  email: '',
  ddi: '',
  phone: '',
  password: '',
  nationalities: [],
  address: '',
  city: '',
  state: '',
  zipcode: '',
});

const nationalities = [
  { id: 1, value: 'Brasileiro (a)' },
  { id: 4, value: 'Argentino (a)' },
  { id: 13, value: 'Português (a)' },
  { id: 12, value: 'Mexicano (a)' },
  { id: 2, value: 'Alemão (a)' },
  { id: 3, value: 'Americano (a)' },
  { id: 5, value: 'Canadense (a)' },
  { id: 6, value: 'Chileno (a)' },
  { id: 7, value: 'Espanhol (a)' },
  { id: 8, value: 'Francês (a)' },
  { id: 9, value: 'Indiano (a)' },
  { id: 10, value: 'Italiano (a)' },
  { id: 11, value: 'Japonês (a)' },
  { id: 14, value: 'Russo (a)' },
  { id: 15, value: 'Sueco (a)' },
  { id: 16, value: 'Suíço (a)' },
  { id: 17, value: 'Outro' },
];

const isLoadingRegister = ref(false);
const registerDataSingleOptIn = ref<boolean>(false);
const isLudopathyTermAccepted = ref<boolean>(false);
const documentField = ref<string>('');

const documentNameOffuscated = computed<string>(() => {
  if (!registerData.value.name) return '';

  const names = registerData.value.name.split(' ');
  const firstName = names[0];
  const remainingNames = names
    .slice(1)
    .map((name: string) => name.replace(/\B\w/g, '*'))
    .join(' ');

  return `${firstName} ${remainingNames}`;
});

const unmaskedCpf = computed<string>(() => {
  const mask = new Mask({ mask: '###.###.###-##' });

  return mask.unmasked(documentField.value);
});

function onDocumentRemoved() {
  registerData.value.name = '';
  registerData.value.document = '';

  resetAntiRobotToken();

  if (isRequestAddressOnRegister.value) {
    addressRef.value?.removeAddress();
  }
}

function onDocumentValidated(data: UserDocument) {
  if (data.name) {
    registerData.value.name = data.name;
  }
  if (data.birth_date) {
    registerData.value.birth_date = data.birth_date;
  }

  registerData.value.document = unmaskedCpf.value;

  resetAntiRobotToken();
}

const isCpfValid = computed<boolean>(() => {
  if (!registerData.value.document) {
    return false;
  }

  return true;
});

const showAllFields = computed<boolean>(() => {
  if (!isSingleStepirstOnlyCpfEnabled.value) {
    return true;
  }

  return isCpfValid.value;
});

const ddiError = ref<string>('');

const selectedDdi = ref({
  id: 4,
  name: 'Brasil',
  code: 'BRA',
  ddi: '+55',
  currency: 'BRL',
  alpha2: 'BR',
});

const maskByCountryPhone = computed<string[] | string>(() => {
  if (!selectedDdi.value.code) return '';

  // @ts-ignore
  return phoneMaksInAlpha3[selectedDdi.value.code] ?? '';
});

function removeMask(str: string, number?: boolean) {
  if (number) {
    return str.replace(/[^\d]/gi, '');
  }

  return str.replace(/[^\w]/gi, '');
}

const { allCountriesList } = storeToRefs(useCountriesStore());

const countriesSortDdi = computed(() => {
  if (!allCountriesList?.value) {
    return;
  }

  return allCountriesList.value.sort((a: any, b: any) => a.ddi - b.ddi);
});

function countMaskLength(mask: any) {
  if (!mask) {
    return 0;
  }

  // Lista de máscaras
  if (Array.isArray(mask)) {
    if (mask.length === 0) return 0;

    return mask.reduce((min, item) => Math.min(min, item.length), Infinity);
  }

  // Função dinâmica (ex.: BRA)
  if (typeof mask === 'function') {
    try {
      const currentMask = mask(registerData.value.phone || '');

      return typeof currentMask === 'string' ? currentMask.length : 0;
    } catch {
      return 0;
    }
  }

  // String única
  if (typeof mask === 'string') {
    return mask.length;
  }

  return 0;
}

const strengthBarRef = ref<InstanceType<typeof StrengthBar> | null>(null);
const isStrengthBarVisible = ref(false);

function updatePasswordStrength() {
  strengthBarRef.value?.updatePasswordStrength();
}

const passwordIsValid = computed(() => {
  return strengthBarRef.value?.passwordIsValid;
});

const addressRef = ref<InstanceType<typeof Address> | null>(null);

const antiRobotRef = ref<InstanceType<typeof AntiRobot> | null>(null);

const antiRobotToken = computed<string>(
  () => antiRobotRef.value?.finalCaptchaTokenValue ?? '',
);

const captchaComponentClass = computed<string>(() => {
  if (!isCaptchaRegisterEnabled.value && !isCaptchaValidateCpfEnabled.value) {
    return 'absolute opacity-0 -z-10 pointer-events-none mt-0 mb-0';
  }

  if (
    isSingleStepirstOnlyCpfEnabled.value &&
    !isCaptchaValidateCpfEnabled.value &&
    !showAllFields.value
  ) {
    return 'absolute opacity-0 -z-10 pointer-events-none mt-0 mb-0';
  }

  if (
    isCaptchaValidateCpfEnabled.value &&
    !isCaptchaRegisterEnabled.value &&
    isCpfValid.value
  ) {
    return 'absolute opacity-0 -z-10 pointer-events-none mt-0 mb-0';
  }

  if (isInvisibleCaptcha.value) {
    if (isSingleStepirstOnlyCpfEnabled.value && !showAllFields.value) {
      return '-mt-2 mb-3.5 relative';
    }

    return '-mt-1 mb-0 relative';
  }

  return 'm-0';
});

function resetAntiRobotToken() {
  antiRobotRef.value?.$resetAllCaptcha();
}

function updateSelectedDate(formattedDate: any) {
  registerData.value.birth_date = formattedDate;
}

function handleErrorRegister(err: any, fallbackMessageKey: string): void {
  console.error(err);

  const errorMessage = err?.data?.message || t(fallbackMessageKey);

  openToaster({
    message: errorMessage,
    type: 'error',
  });

  resetAntiRobotToken();
}

async function handleSuccessRegister(
  res: any,
  successCallback: (res: any) => void,
): Promise<void> {
  if (res.status.toString().startsWith('20')) {
    openToaster({
      message: t('auth.register.success'),
      type: 'success',
    });

    if (res.data) {
      $metrics.push('gtm')?.handle({
        event: 'sign_up',
        ...gtmUserParams(res.data?.user),
      });

      $metrics.push('facebook')?.handle('track', 'Lead', {
        content_name: 'Cadastro',
        status: true,
        value: 1,
      });

      const { isTaboolaEnabled, isKwaiPixelEnabled } = useFeatureFlagsEnv();

      if (isKwaiPixelEnabled.value) {
        $metrics.push('kwai')?.handle('addToCart');
      }

      if (isTaboolaEnabled.value) {
        $metrics.push('taboola')?.handle('lead');
      }

      $metrics.push('appsflyer')?.handle('af_complete_registration', {
        af_registration_method: 'single_step',
        _customer_user_id: res.data?.user?.id,
      });

      if (window.self !== window.top) {
        window.parent.postMessage({
          message: 'loginSuccess',
          responseData: res.data,
          responseHeaders: {
            'X-INFOS-ID':
              res.headers.get('X-INFOS-ID') ||
              res.headers.get('X-Infos-Id') ||
              res.headers.get('x-infos-id'),
            'X-INFOS-REF':
              res.headers.get('X-INFOS-REF') ||
              res.headers.get('X-Infos-Ref') ||
              res.headers.get('x-infos-ref'),
          },
        });
      }

      const { setAuth } = useAuthStore();

      if (isNativeAppEnv() && !isSocialAuthFlow.value) {
        rememberLastCredentials(
          registerData.value.email,
          registerData.value.password,
        );
      }

      await setAuth(res);
      registerUserEvent();

      const { closeAuth } = useAuth();
      const { startSmartico } = useSmarticoInit();

      closeAuth();
      startSmartico();
    }

    successCallback(res);
  } else {
    openToaster({
      message: t('auth.register.error.status_error'),
      type: 'error',
    });
  }
}

const { fieldError, fieldErrorEffect, setFieldError } = useFieldError();

async function handleRegisterSubmit() {
  fieldError.value = '';
  fieldErrorEffect.value = '';

  if (isRegisterRequestDocument.value) {
    if (!registerData.value.document) {
      setFieldError(
        'document',
        t('auth.register.singlestep.errors.type_document'),
      );

      return;
    }
  }

  if (!isEmailValid(registerData.value.email)) {
    setFieldError('email', t('auth.register.singlestep.errors.type_email'));

    return;
  }

  if (isRegisterRequestPhone.value) {
    if (registerData.value.ddi.length < 2) {
      setFieldError('phone', t('auth.register.singlestep.errors.select_ddi'));

      return;
    }

    if (
      registerData.value.phone.length <
      countMaskLength(maskByCountryPhone.value)
    ) {
      setFieldError('phone', t('auth.register.singlestep.errors.type_phone'));

      return;
    }
  }

  if (!isSocialAuthFlow.value) {
    if (!registerData.value.password) {
      setFieldError(
        'password',
        t('auth.register.singlestep.errors.type_password'),
      );

      return;
    }

    if (!passwordIsValid.value) {
      setFieldError(
        'password',
        t('auth.register.singlestep.errors.weak_password'),
      );

      return;
    }
  }

  if (registerData.value.nationalities.length === 0) {
    setFieldError(
      'nationalities',
      t('auth.register.singlestep.errors.select_nationality'),
    );

    return;
  }

  if (!registerDataSingleOptIn.value) {
    setFieldError('terms', t('auth.register.singlestep.errors.accept_terms'));

    return;
  }

  if (!isLudopathyTermAccepted.value && isLudopathyValidationEnabled.value) {
    setFieldError(
      'ludopathy',
      t('auth.register.ludopathy.errors.accept_terms'),
    );

    return;
  }

  if (isCaptchaRegisterEnabled.value && !antiRobotToken.value) {
    setFieldError(
      'captcha',
      t('auth.register.singlestep.errors.complete_captcha'),
    );

    return;
  }

  if (isRequestAddressOnRegister.value) {
    if (!registerData.value.zipcode) {
      setFieldError('address', 'Digite seu CEP');

      return;
    }

    if (
      !registerData.value.address ||
      !registerData.value.city ||
      !registerData.value.state
    ) {
      let message = 'Insira seu endereço completo';

      if (!registerData.value.state) {
        message = 'Selecione seu estado';
      }

      if (!registerData.value.city) {
        message = 'Digite sua cidade';
      }

      if (!registerData.value.address) {
        message = 'Digite o nome da rua';
      }

      setFieldError('address', message);

      await sleep(500);
      addressRef.value?.setEditing();

      return;
    }
  }

  const payload: IRegisterSingleStepSubmitData = {
    country: 'BRA',
    email: registerData.value.email,
    nationalities: JSON.stringify(registerData.value.nationalities),
    optIn: true,
    captcha_token: antiRobotToken.value,
  };

  if (isLudopathyValidationEnabled.value) {
    payload.ludopathy_term_accepted = isLudopathyTermAccepted.value;
  }

  if (isRegisterRequestDocument.value) {
    payload.number = registerData.value.document;
  } else if (isRegisterRequestBirthDate.value) {
    payload.birth_date = registerData.value.birth_date;
  }

  if (isRegisterRequestPhone.value) {
    payload.ddi = registerData.value.ddi;
    payload.phone = removeMask(registerData.value.phone);
  }

  if (isRequestAddressOnRegister.value) {
    payload.zipcode = registerData.value.zipcode;
    payload.address = registerData.value.address;
    payload.city = registerData.value.city;
    payload.state = registerData.value.state;
  }

  if (isSocialAuthFlow.value) {
    payload.email = socialEmail.value;
    payload.token = socialToken.value;
  } else {
    payload.password = registerData.value.password;
  }

  if (referenceCode.value) {
    payload.affiliation_code = String(referenceCode.value);
  }

  const { utmSrc, utmSource, utmCampaign, utmContent, utmMedium, gaClientId } =
    useParameters();

  if (utmSrc.value) {
    payload.src = utmSrc.value;
  }

  if (utmSource.value) {
    payload.utm_source = utmSource.value;
  }

  if (utmCampaign.value) {
    payload.utm_campaign = utmCampaign.value;
  }

  if (utmContent.value) {
    payload.utm_content = utmContent.value;
  }

  if (utmMedium.value) {
    payload.utm_medium = utmMedium.value;
  }

  if (gaClientId.value) {
    payload.ga_client_id = gaClientId.value;
  }

  if (isSocialAuthFlow.value) {
    await initSocialRegister(payload);

    return;
  }

  await initEmailRegister(payload);
}

async function initSocialRegister(payload: any) {
  if (!socialToken.value || !socialService.value) {
    setFieldError(
      'service',
      t('auth.register.singlestep.errors.select_service'),
    );

    return;
  }

  isLoadingRegister.value = true;

  try {
    const res = await ApiService.post(
      `/bff/social/${socialService.value}/registerSimplified`,
      payload,
    );

    await handleSuccessRegister(res, () => {
      if (res.data) {
        clearSocialData();

        return res;
      }
    });
  } catch (err) {
    handleErrorRegister(err, 'auth.register.error.api_error');
    isLoadingRegister.value = false;
  }
}

async function initEmailRegister(payload: any) {
  isLoadingRegister.value = true;

  try {
    const res = await ApiService.post('/bff/register-simplified', payload);

    await handleSuccessRegister(res, () => {
      if (res.data) {
        return res;
      }
    });
  } catch (err) {
    handleErrorRegister(err, 'auth.register.error.api_error');
    isLoadingRegister.value = false;
  }
}

onMounted(() => {
  registerData.value.nationalities = [nationalities[0]];

  if (isSocialAuthFlow.value) {
    registerData.value.email = socialEmail.value;
  }

  // garantir telefone e senha vazios ao iniciar (evitar autofill e cópias cruzadas)
  registerData.value.phone = '';
  if (!isSocialAuthFlow.value) {
    registerData.value.password = '';
  }
});

watch(
  () => selectedDdi.value.ddi,
  () => {
    registerData.value.ddi = selectedDdi.value.ddi;
  },
  { immediate: true },
);

const showTermsModal = ref(false);
</script>

<template>
  <section :class="$style.registerContainer">
    <BaseLoading
      class="!bg-auth-bg-primary z-50"
      :show="isLoadingRegister"
    />

    <div :class="$style.registerWrapper">
      <div :class="$style.registerFields">
        <div
          v-if="showAllFields || isSocialAuthFlow"
          :class="[
            $style.emailField,
            { [$style.fieldHasError]: fieldErrorEffect === 'email' },
          ]"
        >
          <div :class="$style.fieldButton">
            <BaseFormInput
              id="email"
              :key="'email-input'"
              v-model="registerData.email"
              autocapitalize="none"
              autocomplete="email"
              autocorrect="off"
              data-form-type="email"
              :disabled="isSocialAuthFlow"
              :has-error="fieldError === 'email'"
              :initial-value="registerData.email"
              :label="$t('auth.register.email')"
              placeholder="ex: alguem@email.com"
              :required="true"
              spellcheck="false"
              type="email"
              @focus="fieldError = ''"
            />
          </div>

          <div
            v-if="isSocialAuthFlow"
            :class="$style.socialAuthInfo"
          >
            <NuxtIcon
              aria-hidden="true"
              filled
              :name="`social/${socialService}-color`"
            />
          </div>
        </div>
        <div
          v-if="showAllFields"
          :class="$style.rowColumns"
        >
          <div
            v-if="!isSocialAuthFlow"
            :class="[
              $style.passwordContainer,
              { [$style.fieldHasError]: fieldErrorEffect === 'password' },
            ]"
          >
            <div :class="$style.fieldButton">
              <BaseFormInput
                id="password"
                :key="'password-input'"
                v-model="registerData.password"
                autocomplete="new-password"
                :has-error="fieldError === 'password'"
                :initial-value="registerData.password"
                inputmode="text"
                :label="$t('auth.register.password')"
                name="new-password"
                placeholder="crie uma senha segura..."
                :required="true"
                :show-hide-pass="true"
                @blur="isStrengthBarVisible = false"
                @focus="
                  isStrengthBarVisible = true;
                  fieldError = '';
                "
                @keyup="updatePasswordStrength"
              />
            </div>

            <div
              v-show="isStrengthBarVisible"
              :class="$style.strengthBarContainer"
            >
              <AuthStrengthBar
                ref="strengthBarRef"
                :compact-mode="true"
                :password="registerData.password"
              />

              <button
                :class="$style.closeButton"
                @click="isStrengthBarVisible = false"
              >
                {{ $t('auth.register.singlestep.complete') }}
              </button>
            </div>
          </div>

          <div
            :class="[
              $style.nationalitiesContainer,
              { [$style.fieldHasError]: fieldErrorEffect === 'nationalities' },
              { [$style.isSocialFlow]: isSocialAuthFlow },
            ]"
          >
            <div :class="[$style.fieldButton, 'flex-1']">
              <BaseFormSelect
                id="nationalities"
                v-model="registerData.nationalities"
                :button-class="$style.nationalitiesButton"
                :chip-class="$style.nationalityChip"
                :class="$style.clearBg"
                :has-error="fieldError === 'nationalities'"
                :label="$t('auth.register.nationalities')"
                multiple
                :options="nationalities"
                :options-class="$style.selectOptionsOverlay"
                :show-first-only="true"
              >
              </BaseFormSelect>
            </div>
          </div>
        </div>
        <div
          v-if="isRegisterRequestDocument"
          :class="[
            $style.documentContainer,
            { [$style.fieldHasError]: fieldErrorEffect === 'document' },
          ]"
        >
          <div
            v-if="!registerData.document"
            :class="$style.fieldButton"
          >
            <BaseFormInputCpf
              id="cpf"
              v-model:masked="documentField"
              :anti-robot-token="antiRobotToken ?? ''"
              :data-maska-eager="true"
              :has-error="fieldError === 'document'"
              hide-icon
              :label="$t('auth.register.cpf')"
              name="cpf"
              placeholder="ex: 123.456.789-00"
              required
              @focus="fieldError = ''"
              @remove-document="onDocumentRemoved"
              @reset-anti-robot-token="resetAntiRobotToken"
              @validate-document="onDocumentValidated"
            />
          </div>

          <div
            v-else
            :class="$style.documentDetails"
          >
            <div :class="$style.documentValues">
              <div
                v-if="registerData.name"
                :class="$style.documentName"
              >
                {{ documentNameOffuscated }}
              </div>
              <div
                v-if="registerData.document"
                :class="$style.documentVal"
              >
                <b>{{ documentField }}</b>
                <span :class="$style.pipe">|</span>
                <span v-if="registerData.birth_date">
                  {{ getFormattedBrazilianDate(registerData.birth_date) }}
                </span>
                <span
                  v-else
                  :class="$style.hasError"
                >
                  <NuxtIcon name="close" />
                  {{ $t('auth.register.singlestep.errors.birth_date') }}
                </span>
              </div>
            </div>

            <button
              :class="$style.changeButton"
              @click="onDocumentRemoved"
            >
              {{ $t('auth.register.remove_document') }}
            </button>
          </div>
        </div>

        <div
          v-else-if="isRegisterRequestBirthDate"
          :class="[
            $style.birthdateContainer,
            { [$style.fieldHasError]: fieldErrorEffect === 'birth_date' },
          ]"
        >
          <BaseFormSelectBirthdate
            id="birth_date"
            v-model="registerData.birth_date"
            :disabled="false"
            :icon="false"
            :initial-value="registerData.birth_date"
            :label="$t('auth.register.birthdate')"
            :name="'birth_date'"
            :required="true"
            @keydown.enter.prevent=""
            @update:selected-date="updateSelectedDate"
          />
        </div>

        <div
          v-if="isRegisterRequestPhone && showAllFields"
          :class="[
            $style.phoneField,
            { [$style.fieldHasError]: fieldErrorEffect === 'phone' },
          ]"
        >
          <div :class="$style.fieldButton">
            <BaseFormInput
              id="phone"
              :key="'phone-input'"
              v-model="registerData.phone"
              autocapitalize="none"
              autocomplete="off"
              autocorrect="off"
              data-form-type="other"
              :data-maska="maskByCountryPhone"
              :data-maska-eager="true"
              :has-error="fieldError === 'phone'"
              :initial-value="''"
              inputmode="tel"
              :label="$t('auth.register.phone')"
              name="no-autofill-phone"
              pattern="[0-9]*"
              placeholder="ex: (31) 99876-5432"
              :required="true"
              spellcheck="false"
              type="tel"
              @focus="fieldError = ''"
            >
              <template #prepend>
                <HeadlessListbox
                  v-model="selectedDdi"
                  as="div"
                  :class="$style.ddiSelect"
                >
                  <HeadlessListboxButton
                    :class="$style.ddiBtn"
                    @click="ddiError = ''"
                  >
                    <IconWrapper
                      v-if="selectedDdi.code"
                      class="text-xl mr-2"
                      :name="`flags/${selectedDdi.code}`"
                    />
                    {{ selectedDdi.ddi || 'DDI' }}
                    <NuxtIcon
                      v-if="!selectedDdi.code"
                      class="ml-3 opacity-30"
                      name="arrows/angle_down"
                    />
                  </HeadlessListboxButton>

                  <HeadlessListboxOptions :class="$style.ddiOptionsGroup">
                    <div class="px-1 py-1 relative overflow-auto h-full">
                      <HeadlessListboxOption
                        v-for="option in countriesSortDdi"
                        :key="option.id"
                        :value="option"
                      >
                        <span :class="[$style.ddiOption, 'group']">
                          <IconWrapper
                            class="text-xl mr-2"
                            :name="`flags/${option.code}`"
                          />
                          {{ option.ddi }}
                        </span>
                      </HeadlessListboxOption>
                    </div>
                  </HeadlessListboxOptions>
                </HeadlessListbox>
              </template>
            </BaseFormInput>
          </div>
        </div>

        <div
          v-if="isRequestAddressOnRegister"
          v-show="showAllFields"
          :class="[
            $style.addressContainer,
            { [$style.fieldHasError]: fieldErrorEffect === 'address' },
          ]"
        >
          <AuthRegisterSingleStepAddress
            ref="addressRef"
            v-model="registerData"
            :has-error="fieldError === 'address'"
            :typed-cpf="registerData.document"
            @on-focus="fieldError = ''"
          />
        </div>

        <section
          v-if="showAllFields"
          :class="[
            $style.checkboxSection,
            { [$style.hasError]: fieldError === 'terms' },
            { [$style.fieldHasError]: fieldErrorEffect === 'terms' },
          ]"
        >
          <BaseFormCheckbox
            v-model="registerDataSingleOptIn"
            :label="$t('auth.register.singlestep.optin')"
            value="termsAgreement"
          />
        </section>

        <section
          v-if="showAllFields && isLudopathyValidationEnabled"
          :class="[
            $style.checkboxSection,
            { [$style.hasError]: fieldError === 'ludopathy' },
            { [$style.fieldHasError]: fieldErrorEffect === 'ludopathy' },
          ]"
        >
          <BaseFormCheckbox
            v-model="isLudopathyTermAccepted"
            :label="$t('auth.register.ludopathy.optin')"
            value="ludopathy"
          />
        </section>

        <div
          v-if="showAllFields && referenceCode"
          :class="$style.referenceCodeContainer"
        >
          {{ $t('auth.register.reference_code') }}:
          <strong>
            {{ referenceCode }}
          </strong>
        </div>

        <div
          v-if="showAllFields"
          class="self-stretch text-center justify-center"
        >
          <span class="text-white/50 text-xs font-normal leading-none"
            >Ao se registrar você concorda com nossos
          </span>
          <button
            class="text-[#008BFF] text-xs font-bold leading-none underline"
            type="button"
            @click="showTermsModal = true"
          >
            termos e condições
          </button>
        </div>

        <BaseModal
          v-model="showTermsModal"
          :custom-class-wrapper="$style.termsModalWrapper"
          :hide-close-button="true"
          :show-back-button="false"
          :width="720"
        >
          <div :class="$style.termsDialog">
            <div :class="$style.termsHeader">
              <button
                aria-label="Voltar"
                :class="$style.termsBackBtn"
                type="button"
                @click="showTermsModal = false"
              >
                <NuxtIcon
                  class="mr-2 text-[12px] text-white/60"
                  name="arrows/arrow_left"
                />
                <span
                  class="justify-center text-white/70 text-xs font-medium leading-3"
                >
                  Voltar
                </span>
              </button>
              <button
                aria-label="Fechar"
                :class="$style.termsCloseBtn"
                type="button"
                @click="showTermsModal = false"
              >
                <NuxtIcon
                  class="flex text-center justify-center text-white hover:text-white transition font-extrabold text-[20px]"
                  name="close"
                />
              </button>
            </div>
            <div :class="$style.termsBody">
              <PagesTerms route-name="terms" />
            </div>
          </div>
        </BaseModal>

        <div
          v-if="showAllFields"
          class="w-full"
        >
          <button
            class="w-full h-12 px-4 py-3 rounded-[10px] inline-flex justify-center items-center gap-2.5 transition bg-[radial-gradient(ellipse_138.00%_168.30%_at_36.76%_-16.67%,_#FF704A_0%,_#FF3400_100%)] text-white hover:brightness-110"
            @click.prevent="handleRegisterSubmit"
          >
            <span
              class="text-sm sm:text-base font-semibold leading-none truncate uppercase"
            >
              {{ $t('auth.register.singlestep.register') }}
            </span>
          </button>
        </div>

        <div :class="captchaComponentClass">
          <BaseFormAntiRobot
            ref="antiRobotRef"
            :field-has-error="fieldErrorEffect === 'captcha'"
          />
        </div>

        <div
          v-if="!showAllFields"
          :class="$style.buttonsContainer"
        >
          <div class="alert alert-success whitespace-normal">
            Por favor, digite seu CPF para continuar...
          </div>
        </div>

        <template v-if="isSocialAuthEnabled">
          <div :class="$style.separator" />
          <AuthSocialAuth
            :is-login="false"
            with-titles
          />
        </template>

        <BaseModalFooter v-if="!isAuthV2FlowEnabled" />
      </div>
    </div>
  </section>
</template>

<style module lang="postcss">
.registerContainer {
  @apply w-full flex flex-col min-h-full;

  .registerWrapper {
    @apply w-full flex flex-col gap-3 mt-5;

    .orDivider {
      @apply flex items-center justify-center text-xs font-semibold text-auth-texts/40 my-3 md:my-4 uppercase leading-3;

      &:before,
      &:after {
        content: '';
        @apply h-[1px] bg-auth-texts/10 flex-1 mt-0.5;
      }
    }

    .separator {
      @apply h-0 mb-3;
    }

    .registerFields {
      @apply flex flex-col gap-2.5 md:gap-3;

      .fieldHasError {
        @apply border-error;
        animation: shake 200ms ease-in-out;
      }

      .birthdateContainer {
        @apply relative w-full;
      }

      .documentContainer {
        @apply flex flex-col;

        .documentDetails {
          @apply flex justify-between items-center gap-0.5 sm:gap-2 relative bg-[#0C1B44] rounded-md w-full min-h-[46px] sm:min-h-[50px] h-auto py-1;

          .documentValues {
            @apply flex flex-col gap-0 sm:gap-px px-5 pr-24 text-2xs flex-1 min-w-0;

            .documentName {
              @apply text-auth-texts/60;
            }

            .documentVal {
              @apply text-auth-texts font-semibold flex flex-wrap items-start whitespace-normal w-full min-w-0 gap-1 text-sm md:text-base;

              b {
                @apply text-primary font-semibold whitespace-nowrap text-base md:text-lg;
              }

              .pipe {
                @apply text-auth-texts/30 mx-2;
              }

              .hasError {
                @apply text-error text-2xs inline-flex items-center gap-1;
              }
            }
          }

          .changeButton {
            @apply absolute right-4 text-[.6rem] text-auth-text-inputs flex justify-center items-center rounded px-2 h-5 text-nowrap flex-nowrap bg-auth-text-inputs/20 whitespace-nowrap;
          }
        }
      }

      .emailField {
        @apply relative;

        .socialAuthInfo {
          @apply absolute right-0 top-0 text-lg flex justify-center items-center w-[46px] sm:w-[50px] h-[46px] sm:h-[50px];
        }
      }

      .ddiSelect {
        @apply relative inline-block text-left text-texts border-solid border-r border-auth-text-inputs/20;
        .ddiBtn {
          @apply inline-flex w-full justify-center items-center rounded-none px-4 py-2 text-sm font-medium;
        }
        .ddiOptionsGroup {
          @apply absolute left-0 mt-2 w-24 origin-top-right rounded-md bg-[#0C1B44] shadow-none ring-0 focus:outline-none z-[999] overflow-hidden h-[200px] divide-y-0;
        }
        .ddiOption {
          @apply flex w-full items-center rounded-md px-2 py-2 text-sm cursor-pointer;
        }
      }

      .rowColumns {
        @apply flex flex-col md:flex-row gap-2 md:gap-3 items-stretch md:items-center justify-center max-w-full relative;

        .passwordContainer {
          @apply relative z-10 flex-1 md:flex-[2] w-full min-w-0;

          .strengthBarContainer {
            @apply absolute top-full left-0 w-full bg-[#0C1B44] p-3 -mt-2 rounded-b-md shadow-lg shadow-black/50;

            .closeButton {
              @apply text-xs text-auth-text-inputs border border-auth-text-inputs/50 rounded-md px-2 py-1 mt-3;
            }
          }
        }

        .nationalitiesContainer {
          @apply flex gap-2 md:gap-3 items-center justify-center max-w-full relative w-full md:min-w-40 md:flex-[1] min-w-0;

          &.isSocialFlow {
            @apply min-w-full;
          }
        }
      }

      .addressContainer {
        @apply relative w-full;
      }

      .checkboxSection {
        @apply px-3 py-2 rounded-lg flex items-start;
        background-color: #0c1b44 !important;

        &.hasError {
          box-shadow: 0 0 200px inset rgba(255, 0, 0, 0.1);
        }

        p {
          @apply text-2xs leading-4;
        }

        a {
          @apply text-primary underline cursor-pointer whitespace-nowrap;
        }

        b {
          @apply font-semibold text-primary;
        }
      }

      .referenceCodeContainer {
        @apply text-auth-texts/50 text-2xs leading-3 font-medium flex justify-center gap-1.5 relative bg-[#0C1B44] rounded-md whitespace-nowrap px-3 py-2;

        strong {
          @apply text-primary font-semibold overflow-hidden overflow-ellipsis;
        }
      }

      .buttonsContainer {
        @apply w-full flex justify-center mx-auto whitespace-nowrap;

        button {
          /* novo visual */
          @apply self-stretch h-12 p-5 bg-[#0C1B44] rounded-[10px]
           inline-flex justify-start items-center gap-2.5
           text-white font-semibold w-full;

          &:disabled {
            @apply opacity-40 cursor-not-allowed;
          }
        }
      }

      .fieldButton {
        @apply w-full h-[46px] sm:h-[50px] bg-[#0C1B44] rounded-[10px] px-5 flex items-center gap-2.5;
      }

      /* força transparência no root dos componentes base quando passamos a classe no template */
      .clearBg {
        background-color: transparent !important;
        box-shadow: none !important;
        border: none !important;
      }

      /* Estilo fixo dos campos somente neste SingleStep */
      .emailField :global(.input-group),
      .phoneField :global(.input-group),
      .passwordContainer :global(.input-group),
      .documentContainer :global(.input-group) {
        background-color: transparent !important;
        box-shadow: none !important;
        height: 100%;
        width: 100%;
        border-radius: inherit;
      }

      .emailField :global(.group),
      .phoneField :global(.group),
      .passwordContainer :global(.group),
      .documentContainer :global(.group) {
        @apply h-[46px] sm:h-[50px] px-0 bg-transparent;
      }

      .emailField :global(.input),
      .phoneField :global(.input),
      .passwordContainer :global(.input),
      .documentContainer :global(.input) {
        @apply text-white placeholder-white/70;
      }

      .emailField :global(.label),
      .phoneField :global(.label),
      .passwordContainer :global(.label) {
        @apply text-white/70;
      }

      /* Remover asterisco de obrigatório nestes campos */
      .emailField :global(.required),
      .documentContainer :global(.required),
      .phoneField :global(.required),
      .passwordContainer :global(.required),
      .birthdateContainer :global(.required) {
        display: none !important;
      }

      /* Forçar cor de fundo correta para data de nascimento */
      .birthdateContainer :global(.input-group),
      .birthdateContainer :global(.group) {
        background-color: #0c1b44 !important;
        @apply h-[46px] sm:h-[50px];
      }

      .birthdateContainer :global(.input),
      .birthdateContainer :global(select) {
        background-color: #0c1b44 !important;
        @apply text-white placeholder-white/70;
      }

      .birthdateContainer :global(.label) {
        @apply text-white/70;
      }

      /* Override autofill para data de nascimento */
      .birthdateContainer :global(select:-webkit-autofill),
      .birthdateContainer :global(select:-webkit-autofill:hover),
      .birthdateContainer :global(select:-webkit-autofill:focus),
      .birthdateContainer :global(select:-webkit-autofill:active) {
        -webkit-box-shadow: 0 0 0 1000px #0c1b44 inset !important;
        box-shadow: 0 0 0 1000px #0c1b44 inset !important;
        -webkit-text-fill-color: #ffffff !important;
      }

      /* Remover sombra/overlay de erro no email para manter fundo #0C1B44 */
      .emailField :global(.input-group.has-error),
      .emailField :global(.input.has-error) {
        box-shadow: none !important;
      }

      /* Ajustar altura e fonte do input CPF para não parecer pequeno */
      .documentContainer :global(.input) {
        @apply h-[52px] sm:h-[50px] text-base;
      }
      .documentContainer :global(.input:focus) {
        @apply text-lg;
      }

      /* Override do autofill também para o campo Email */
      .emailField :global(input:-webkit-autofill),
      .emailField :global(input:-webkit-autofill:hover),
      .emailField :global(input:-webkit-autofill:focus),
      .emailField :global(input:-webkit-autofill:active) {
        -webkit-box-shadow: 0 0 0 1000px #0c1b44 inset !important;
        box-shadow: 0 0 0 1000px #0c1b44 inset !important;
        -webkit-text-fill-color: #ffffff !important;
      }

      /* Select de nacionalidades */
      .nationalitiesContainer :global(.editableField) {
        background-color: transparent !important;
        box-shadow: none !important;
        height: auto !important;
        min-height: 46px;
        width: 100%;
        border-radius: inherit;
        align-items: flex-start;
      }

      /* Permitir altura dinâmica para múltiplas nacionalidades selecionadas */
      .nationalitiesContainer .fieldButton {
        @apply min-h-[46px] sm:min-h-[50px] h-auto py-0;
      }

      .nationalitiesContainer :global(.listBoxButton) {
        @apply px-0 text-white min-h-[46px] sm:min-h-[50px] h-auto py-0 items-start;
        max-height: 88px;
        overflow: hidden; /* impede o crescimento e mantém o ícone fixo */
      }
      @media (min-width: 768px) {
        .nationalitiesContainer :global(.listBoxButton) {
          max-height: 120px;
        }
      }
      .nationalitiesContainer :global(.contentWrap) {
        max-height: 88px;
        overflow-y: auto; /* rolagem apenas dos chips/conteúdo */
      }
      @media (min-width: 768px) {
        .nationalitiesContainer :global(.contentWrap) {
          max-height: 120px;
        }
      }
      /* Permitir quebra de linha do texto selecionado (remover truncate/max-width) */
      .nationalitiesContainer :global(.listBoxButton p) {
        white-space: normal !important;
        overflow: visible !important;
        text-overflow: clip !important;
        max-width: 100% !important;
      }

      /* Classes específicas para o Select de nacionalidades */
      .nationalitiesButton {
        @apply px-0 min-h-[46px] sm:min-h-[50px] h-auto py-0 items-start;
        overflow: hidden; /* evita que o botão cresça junto com os chips */
      }
      /* Rolagem apenas do conteúdo interno (chips), mantendo ícone/arrow fixo */
      .nationalitiesButton > div {
        max-height: 88px; /* ~2 linhas no mobile */
        overflow-y: auto; /* rolagem interna apenas dos chips/texto */
      }
      @media (min-width: 768px) {
        .nationalitiesButton > div {
          max-height: 120px; /* ~3 linhas no desktop */
        }
      }

      .nationalityChip {
        @apply bg-transparent border border-auth-text-inputs/30 text-white text-2xs md:text-xs px-1.5 py-0.5 rounded;
      }

      /* Override do painel de opções do select de nacionalidades */
      .selectOptionsOverlay {
        @apply bg-[#0C1B44] ring-0 shadow-none divide-y-0 z-[999] max-h-[280px] overflow-auto rounded-md border border-auth-text-inputs/20;
      }

      /* limpar backgrounds de prefix/prepend/suffix para não ficar cinza */
      .emailField :global(.prefix),
      .phoneField :global(.prefix),
      .passwordContainer :global(.prefix),
      .documentContainer :global(.prefix),
      .emailField :global(.prepend),
      .phoneField :global(.prepend),
      .passwordContainer :global(.prepend),
      .documentContainer :global(.prepend),
      .emailField :global(.suffix),
      .phoneField :global(.suffix),
      .passwordContainer :global(.suffix) {
        background-color: transparent !important;
        border-color: transparent !important;
      }

      /* remover qualquer borda/linha do container do input */
      .emailField :global(.group),
      .phoneField :global(.group),
      .passwordContainer :global(.group) {
        border: none !important;
      }

      /* Forçar fundo correto nos campos Telefone e Senha (inclui autofill) */
      .phoneField :global(.input-group),
      .passwordContainer :global(.input-group),
      .phoneField :global(.group),
      .passwordContainer :global(.group) {
        background-color: #0c1b44 !important;
      }

      /* Fundo correto do campo CPF */
      .documentContainer :global(.input-group),
      .documentContainer :global(.group) {
        background-color: #0c1b44 !important;
        @apply h-[46px] sm:h-[50px];
      }
      /* Aumentar altura do wrapper do CPF */
      .documentContainer .fieldButton {
        @apply h-[46px] sm:h-[50px];
      }
      /* Remover efeito de prefix reduzindo largura do input CPF */
      .documentContainer :global(.group.prefix) {
        @apply rounded-l-md max-w-full pl-4;
      }

      /* Espaço entre DDI e conteúdo do telefone (aumentado) */
      .phoneField :global(.group.prefix) {
        @apply pl-5 sm:pl-6;
      }

      /* Evitar dim (opacidade) no CPF readonly */
      .documentContainer :global(.group[readonly]:not([readonly='false'])),
      .documentContainer :global(.input[readonly]:not([readonly='false'])) {
        opacity: 1 !important;
      }
      /* Garantir fundo transparente também em estado readonly */
      .documentContainer :global(.input[readonly]) {
        background-color: transparent !important;
      }

      /* input transparente (o fundo vem do container acima) */
      .phoneField :global(input),
      .passwordContainer :global(input),
      .documentContainer :global(input) {
        background-color: transparent !important;
      }

      /* Override do autofill que aplicava bg cinza */
      .phoneField :global(input:-webkit-autofill),
      .passwordContainer :global(input:-webkit-autofill),
      .documentContainer :global(input:-webkit-autofill),
      .phoneField :global(input:-webkit-autofill:hover),
      .passwordContainer :global(input:-webkit-autofill:hover),
      .documentContainer :global(input:-webkit-autofill:hover),
      .phoneField :global(input:-webkit-autofill:focus),
      .passwordContainer :global(input:-webkit-autofill:focus),
      .documentContainer :global(input:-webkit-autofill:focus),
      .phoneField :global(input:-webkit-autofill:active),
      .passwordContainer :global(input:-webkit-autofill:active),
      .documentContainer :global(input:-webkit-autofill:active) {
        -webkit-box-shadow: 0 0 0 1000px #0c1b44 inset !important;
        box-shadow: 0 0 0 1000px #0c1b44 inset !important;
        -webkit-text-fill-color: #ffffff !important;
      }
    }
  }
}

.termsModalWrapper {
  @apply max-w-full;
}
@media (max-width: 767px) {
  .termsModalWrapper {
    @apply w-[96vw];
  }

  /* Evitar dupla rolagem no mobile e usar a altura útil da viewport */
  .termsModalWrapper :global(.inner) {
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: 100svh;
  }
}

.termsHeader {
  @apply sticky top-0 z-20 bg-bg-primary/90 backdrop-blur flex w-full justify-between items-center gap-5 px-4 py-3;
}

.termsBackBtn {
  @apply px-2.5 py-[5px] bg-white/5 rounded-full inline-flex justify-center items-center gap-1 cursor-pointer;
}

.termsCloseBtn {
  @apply flex items-center justify-center cursor-pointer w-5 h-5;
}

/* Container flex para o conteúdo do modal */
.termsDialog {
  display: flex;
  flex-direction: column;
  height: 100svh;
  height: 100dvh; /* fallback cross-browser */
  max-height: 100svh;
}

/* Desktop: usa altura um pouco menor para não colar nas bordas */
@media (min-width: 768px) {
  .termsDialog {
    height: 88vh;
    max-height: 88vh;
  }

  /* Sincroniza a altura do container interno do BaseModal com o wrapper do modal */
  .termsModalWrapper :global(.inner) {
    max-height: 88vh;
    display: flex;
    flex-direction: column;
  }
}

/* Ajustes para modal de Termos: fixar lateral direita e rolar apenas conteúdo esquerdo,
   além de reduzir espaçamento excessivo entre parágrafos. */
.termsBody {
  @apply min-h-0 flex-1;
  overflow: hidden;
}

/* Desktop: duas colunas e rolagem apenas na esquerda */
@media (min-width: 768px) {
  .termsBody :global(.boxesWrapper) {
    display: grid !important;
    grid-template-columns: minmax(0, 1fr) 250px !important;
    align-items: start !important;
    gap: 16px !important;
    height: 100%; /* ocupar toda área disponível do body do modal */
  }

  /* Conteúdo esquerdo (texto dos termos) com rolagem apenas dentro do box */
  .termsBody :global(.box) {
    height: 100%;
    overflow-y: auto;
    padding-right: 8px; /* espaço para scrollbar */
  }

  /* Lateral direita fixa */
  .termsBody :global(.pageSidebar) {
    position: sticky;
    top: 0;
    align-self: flex-start;
    max-height: 100%;
    overflow: hidden;
  }
}

/* Mobile: mantém rolagem total (coluna única) */
@media (max-width: 767px) {
  .termsBody {
    @apply max-h-[100dvh];
    overflow-y: auto;
    padding-right: 8px;
    overscroll-behavior: contain; /* evita scroll do body por trás */
    -webkit-overflow-scrolling: touch; /* rolagem suave no iOS */
    padding-bottom: env(safe-area-inset-bottom); /* usa área segura no iOS */
  }
}

/* Reduzir espaçamento: limitar margens para ~2 linhas visuais */
.termsBody :global(.pageSlotWrapper p),
.termsBody :global(.pageSlotWrapper ul),
.termsBody :global(.pageSlotWrapper ol),
.termsBody :global(.pageSlotWrapper table),
.termsBody :global(.pageSlotWrapper span),
.termsBody :global(.pageSlotWrapper strong),
.termsBody :global(.pageSlotWrapper b),
.termsBody :global(.pageSlotWrapper em) {
  margin-bottom: 0.5rem !important;
}

.termsBody :global(.pageSlotWrapper h1),
.termsBody :global(.pageSlotWrapper h2),
.termsBody :global(.pageSlotWrapper h3),
.termsBody :global(.pageSlotWrapper h4),
.termsBody :global(.pageSlotWrapper h5),
.termsBody :global(.pageSlotWrapper h6) {
  margin-top: 0.5rem !important;
  margin-bottom: 0.75rem !important;
}

/* Remover blocos vazios para evitar buracos */
.termsBody :global(.pageSlotWrapper p:empty),
.termsBody :global(.pageSlotWrapper li:empty) {
  display: none !important;
}

/* Colapsar quebras múltiplas: esconde 2+ <br> seguidos (deixa no máximo 1) */
.termsBody :global(.pageSlotWrapper br + br) {
  display: none;
}

@keyframes shake {
  0% {
    transform: translateX(-3px);
  }
  25% {
    transform: translateX(3px);
  }
  50% {
    transform: translateX(-3px);
  }
  75% {
    transform: translateX(3px);
  }
  100% {
    transform: translateX(0);
  }
}
</style>
